import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

import {
  ProjectActionButtons,
  type XmlDownloadAvailability,
} from './ProjectActionButtons';
import { ProjectEditActions } from './ProjectEditActions';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import type { ProjectFeedbackProps } from '../../hooks/survey';
import type { FingeringSize } from '../FingeringOverlay';
import { ProjectPlaybackActions } from './ProjectPlaybackActions';

export type ProjectToolbarMode = 'normal' | 'edit' | 'playback';

export interface ProjectToolbarProps {
  mode: ProjectToolbarMode;
  projectName: string;
  /** Fired when the back pill is activated (telemetry / navigation side effects). */
  onBack: () => void;
  /** Back pill target. Defaults to the workspace; sample pages point home. */
  backTo?: string;
  /** Back pill label. Defaults to 'My Project'. */
  backLabel?: string;

  // Font Size popover.
  // The XML project page has no font-size control, so it passes
  // `showFontSize={false}` and the button + popover are skipped
  // entirely. The remaining Font Size props are ignored in that
  // case but stay required so the PDF page's call site (which
  // always uses the font size) can't accidentally drop them.
  showFontSize?: boolean;
  fontPopoverOpen: boolean;
  onFontPopoverOpenChange: (open: boolean) => void;
  fontSize: FingeringSize;
  onFontSizeChange: (next: FingeringSize) => void;
  /** Pitch-letter slider. Hidden when the callback is omitted. */
  pitchSize?: FingeringSize;
  onPitchSizeChange?: (next: FingeringSize) => void;

  // Print button. When `onPrint` is defined, a Print icon button is
  // rendered to the LEFT of the Download button, using the same
  // ghost icon-button styling. Used by the XML project page; the
  // PDF page omits this prop and the button isn't rendered.
  onPrint?: () => void;

  // Help / tutorial button. When `onShowTutorial` is defined, a "?"
  // icon button is rendered at the end of the right-side icon group
  // (same ghost styling) so users can replay the onboarding tour.
  onShowTutorial?: () => void;

  // Download menu (dropdown opened from the toolbar's download
  // button — exposes Download PDF and, when available, Download XML
  // and Download XML for Sibelius). All three handlers are optional:
  // the download button is hidden entirely if none is provided, and
  // the dropdown only renders the items whose handlers are defined.
  downloadPopoverOpen: boolean;
  onDownloadPopoverOpenChange: (open: boolean) => void;
  onDownloadPdf?: () => void;
  isDownloadingPdf: boolean;
  /** Optional. When omitted, the XML option is hidden from the menu
   *  (e.g. for projects whose XML output isn't available yet). */
  onDownloadXml?: () => void;
  isDownloadingXml: boolean;
  /** Optional. The Sibelius-compatible MusicXML download. Provided
   *  alongside `onDownloadXml` whenever the project has XML output;
   *  see DownloadMenuPopover for the menu item rendering. */
  onDownloadXmlSibelius?: () => void;
  isDownloadingXmlSibelius?: boolean;
  onDownloadXmlPlain?: () => void;
  isDownloadingXmlPlain?: boolean;
  /** Shown after an engraved render has produced a MIDI file. */
  onDownloadMidi?: () => void;
  xmlAvailability?: XmlDownloadAvailability;

  // Hidden when omitted — sample pages have no project to attach
  // a response to.
  feedback?: ProjectFeedbackProps;

  // Edit-mode actions (only consulted when mode === 'edit')
  onReset: () => void;
  canReset: boolean;
  onUndo: () => void;
  canUndo: boolean;
  onDone: () => void;
  isSaving: boolean;

  /** Consulted when mode === 'playback'. */
  isPlaying?: boolean;
  canPlay?: boolean;
  playbackLoading?: boolean;
  playbackStatus?: string | null;
  onTogglePlay?: () => void;
  /** Leaves playback. Shown beside Play / Pause. */
  onExitPlayback?: () => void;
}

/**
 * Top toolbar for the PDF project page. Two visual modes:
 *
 * - `normal`: My Project pill on the left, project title centred, and a
 *   row of ghost icon buttons on the right (Font Size, Download,
 *   Feedback). Full Screen / Print are intentionally omitted for now.
 *
 * - `edit`: My Project + title fade to a non-interactive ghost so the
 *   user's attention is on the centred Reset / Undo / Done trio. The
 *   right-side icon group is hidden so it doesn't compete for attention.
 *
 * - `playback`: same chrome lock as edit. The centre is Exit, Play,
 *   and Pause. Score taps set the start cursor and do not start audio.
 *
 * Pinned to the viewport top (`position: fixed`) so pinch/wheel zoom
 * cannot scroll it away. An in-flow spacer keeps the score below it.
 *
 * The Mobile reference is `MobileApp/components/project/ProjectToolbar.tsx`
 * (resting state) and `EditToolbar.tsx` (edit state); the edit-mode
 * semantics (Reset reverts to entry snapshot, Undo pops the in-session
 * stack, Done auto-saves and exits) are documented in
 * `MobileApp/components/project/fingeringWebViewHtml.ts` lines 943-974.
 */
export const ProjectToolbar: React.FC<ProjectToolbarProps> = ({
  mode,
  projectName,
  onBack,
  backTo = '/workspace',
  backLabel = 'My Project',
  showFontSize = true,
  fontPopoverOpen,
  onFontPopoverOpenChange,
  fontSize,
  onFontSizeChange,
  pitchSize,
  onPitchSizeChange,
  onPrint,
  onShowTutorial,
  downloadPopoverOpen,
  onDownloadPopoverOpenChange,
  onDownloadPdf,
  isDownloadingPdf,
  onDownloadXml,
  isDownloadingXml,
  onDownloadXmlSibelius,
  isDownloadingXmlSibelius = false,
  onDownloadXmlPlain,
  isDownloadingXmlPlain = false,
  onDownloadMidi,
  xmlAvailability,
  feedback,
  onReset,
  canReset,
  onUndo,
  canUndo,
  onDone,
  isSaving,
  isPlaying = false,
  canPlay = false,
  playbackLoading = false,
  playbackStatus = null,
  onTogglePlay,
  onExitPlayback,
}) => {
  const isEdit = mode === 'edit';
  const isPlayback = mode === 'playback';
  const lockNavigation = isEdit || isPlayback;
  // Below `md` these groups live in the fixed bottom bar instead; mounting them
  // here too would duplicate the font/download popovers and their listeners.
  const isDesktop = useIsDesktop();

  return (
    <>
      {/* Viewport-fixed so zoom's window.scrollTo cannot carry the title
          bar off-screen. An ancestor overflow-x on the page shell also
          breaks `sticky` (it becomes a scrollport), which is why this
          is `fixed` rather than `sticky top-0`. */}
      <div
        className={`fixed top-0 z-50 w-full border-y border-black/20 bg-white`}
      >
        <div
          className={`
            mx-auto flex w-full max-w-7xl items-center gap-x-6 px-4 py-2
          `}
        >
          {/* Left: back pill. Guests and samples go Home; members go
            to the workspace. Faded + non-interactive in edit mode
            so the user can't accidentally bail mid-edit. */}
          <Link
            to={backTo}
            onClick={(e) => {
              if (lockNavigation) {
                e.preventDefault();
                return;
              }
              onBack();
            }}
            aria-disabled={lockNavigation}
            tabIndex={lockNavigation ? -1 : 0}
            className={`
              inline-flex shrink-0 items-center gap-x-2 rounded-full border
              border-gray-700 bg-white px-5 py-2 text-sm font-medium
              text-gray-700 transition-colors
              hover:bg-gray-50
              ${lockNavigation ? 'pointer-events-none opacity-40' : ''}
          `}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
            <span>{backLabel}</span>
          </Link>

          {/* Center / right area swaps based on mode. In edit mode the
            Reset / Undo / Done group only shows at `md`+; below that it
            moves into the fixed bottom bar (see ProjectMobileBottomBar). */}
          {isEdit ? (
            isDesktop && (
              <ProjectEditActions
                className="flex-1"
                onReset={onReset}
                canReset={canReset}
                onUndo={onUndo}
                canUndo={canUndo}
                onDone={onDone}
                isSaving={isSaving}
              />
            )
          ) : isPlayback ? (
            isDesktop &&
            onTogglePlay && (
              <ProjectPlaybackActions
                isPlaying={isPlaying}
                canPlay={canPlay}
                isLoading={playbackLoading}
                statusText={playbackStatus}
                onTogglePlay={onTogglePlay}
                onExit={onExitPlayback}
              />
            )
          ) : (
            <>
              {/* Center: project title. `min-w-0` lets the truncate
                kick in when the title is long. */}
              <div className="flex min-w-0 flex-1 items-center">
                <h1
                  className={`truncate text-lg font-medium text-gray-700`}
                  title={projectName || 'Untitled Project'}
                  // A user's project name is not page text; the fallback is.
                  translate={projectName ? 'no' : undefined}
                >
                  {projectName || 'Untitled Project'}
                </h1>
              </div>

              {/* Right: Font Size / Print / Download / Feedback / Help
                icon group. Mounted only at `md`+ — on small screens
                these actions move into the fixed bottom bar. */}
              {isDesktop && (
                <ProjectActionButtons
                  showFontSize={showFontSize}
                  fontPopoverOpen={fontPopoverOpen}
                  onFontPopoverOpenChange={onFontPopoverOpenChange}
                  fontSize={fontSize}
                  onFontSizeChange={onFontSizeChange}
                  pitchSize={pitchSize}
                  onPitchSizeChange={onPitchSizeChange}
                  onPrint={onPrint}
                  onShowTutorial={onShowTutorial}
                  downloadPopoverOpen={downloadPopoverOpen}
                  onDownloadPopoverOpenChange={onDownloadPopoverOpenChange}
                  onDownloadPdf={onDownloadPdf}
                  isDownloadingPdf={isDownloadingPdf}
                  onDownloadXml={onDownloadXml}
                  isDownloadingXml={isDownloadingXml}
                  onDownloadXmlSibelius={onDownloadXmlSibelius}
                  isDownloadingXmlSibelius={isDownloadingXmlSibelius}
                  onDownloadXmlPlain={onDownloadXmlPlain}
                  isDownloadingXmlPlain={isDownloadingXmlPlain}
                  onDownloadMidi={onDownloadMidi}
                  xmlAvailability={xmlAvailability}
                  feedback={feedback}
                  popoverPlacement="bottom"
                />
              )}
            </>
          )}
        </div>
      </div>
      {/* In-flow stand-in: same vertical chrome as the back-pill row so
          the score does not slide under the fixed bar. */}
      <div
        aria-hidden
        className="pointer-events-none invisible w-full border-y px-4 py-2"
      >
        <div className="inline-flex items-center px-5 py-2 text-sm">
          <span>{backLabel}</span>
        </div>
      </div>
    </>
  );
};
